import { useState } from "react";
import type { FunctionComponent } from "react";
import { GraduationCap, Zap } from "lucide-react";
import useSettingsStore from "../../stores/useSettingsStore";
import type { CoachLevel } from "../../stores/useSettingsStore";
import { linkStyles } from "./linkStyles";
import type { LinkSuccess } from "./types";

interface Props {
  readonly linkResult: LinkSuccess;
  readonly onComplete: () => void;
}

const DESCRIPTIONS: Record<CoachLevel, string> = {
  beginner: "Chess terms are explained inline. Avoids jargon. Best for players new to chess strategy.",
  advanced: "Concise analysis using standard chess terminology. Best for experienced players.",
};

export const CoachLevelStep: FunctionComponent<Props> = ({ linkResult, onComplete }) => {
  const recommended: CoachLevel = linkResult.coachLevel === "advanced" ? "advanced" : "beginner";
  const [selectedLevel, setSelectedLevel] = useState<CoachLevel>(recommended);

  const handleSave = () => {
    useSettingsStore.getState().setCoachLevel(selectedLevel);
    onComplete();
  };

  return (
    <div style={{ padding: "12px 0" }}>
      <div style={linkStyles.successMessage}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>Linked successfully!</span>
      </div>

      <div style={linkStyles.coachLevelSection}>
        <p style={linkStyles.coachLevelRecommendation}>
          {linkResult.rating
            ? `Based on your rating (~${linkResult.rating}), we recommend `
            : "We recommend "}
          <strong style={{ color: "var(--color-text-primary)" }}>
            {linkResult.coachLevel === "advanced" ? "Advanced" : "Beginner"}
          </strong> mode.
        </p>

        <div style={linkStyles.coachLevelToggle}>
          {(["beginner", "advanced"] as CoachLevel[]).map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => setSelectedLevel(level)}
              style={{
                ...linkStyles.coachLevelToggleBtn,
                ...(selectedLevel === level ? linkStyles.coachLevelToggleBtnActive : {}),
              }}
            >
              {level === "beginner"
                ? <GraduationCap size={16} className="mr-1 inline-block align-text-bottom" />
                : <Zap size={16} className="mr-1 inline-block align-text-bottom" />}
              {level === "beginner" ? "Beginner" : "Advanced"}
            </button>
          ))}
        </div>

        <p style={linkStyles.coachLevelDescription}>
          {DESCRIPTIONS[selectedLevel]}
        </p>

        <button type="button" onClick={handleSave} style={linkStyles.saveButton}>
          Save &amp; Continue
        </button>
      </div>
    </div>
  );
};
